/**
 * Activation readiness checklist — shared between the package editor
 * (PackageEdit) and the grouping editor's Packages tab (GroupingEdit).
 *
 * The backend `/portal/reseller/packages/activation-readiness` endpoint
 * returns each item with a severity:
 *   - 'required' → blocks setting Status='active'. Red.
 *   - 'warning'  → doesn't block, but flags follow-up before the
 *                  related downstream event (e.g. Mode A bank info,
 *                  needed before the first weekly EFT runs). Yellow.
 *
 * The whole banner hides itself when every item is met.
 */
import { Link } from 'react-router-dom'

export default function ActivationChecklist({ activation }) {
  if (!activation) return null
  const unmet = (activation.items || []).filter(i => !i.met)
  if (unmet.length === 0) return null

  // Banner style switches to red if any required item is unmet,
  // yellow if all unmet items are warnings only.
  const hasBlocker = unmet.some(i => i.severity === 'required')
  const palette = hasBlocker
    ? { bg: '#fef2f2', border: '#f87171', heading: '#991b1b', body: '#7f1d1d', link: '#991b1b' }
    : { bg: '#fffbeb', border: '#f59e0b', heading: '#92400e', body: '#78350f', link: '#b45309' }

  return (
    <div style={{
      background: palette.bg,
      border: '1px solid ' + palette.border,
      borderRadius: 8,
      padding: 14,
      marginBottom: 16,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: palette.heading, marginBottom: 8 }}>
        {hasBlocker
          ? 'Before you can activate a package, complete the following:'
          : 'Heads up — these aren\'t required to activate, but you\'ll need them soon:'}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: palette.body, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {(activation.items || []).map(it => {
          // Per-item color: required-unmet red, warning-unmet yellow,
          // met grey. The banner color above is for the most severe
          // unmet item in the list.
          const itemColor = it.met
            ? '#94a3b8'
            : it.severity === 'required' ? '#dc2626' : '#b45309'
          return (
            <li key={it.key} style={{ color: itemColor, opacity: it.met ? 0.7 : 1 }}>
              <span style={{ marginRight: 6 }}>{it.met ? '✓' : it.severity === 'required' ? '●' : '○'}</span>
              {it.label}
              {!it.met && it.helpHref && (
                <Link to={it.helpHref} style={{ marginLeft: 8, color: palette.link, fontWeight: 600, textDecoration: 'underline' }}>
                  Set up →
                </Link>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
